<template>
  <div class="wealth-invest">
    <NavBar v-if="renderAsView" hide-border>
      <template #left>
        <BackButton />
      </template>
    </NavBar>
    <main :class="{ 'wealth-invest__content': renderAsView }">
      <Heading v-if="renderAsView" class="wealth-invest__title">
        Escolha a carteira para investir
      </Heading>
      <LoaderIndicator v-if="isLoading" with-container size="32" />
      <HelperViewStatus
        v-else-if="hasError"
        class="wealth-invest__error"
        generic-error
        button-label="Tentar novamente"
        @click="fetchPortfolios"
      />
      <Grid v-else :columns="[1, 1]" gutter="xm">
        <PortfolioCardV2
          v-for="(portfolio, index) in portfolioList"
          :key="index"
          class="wealth-invest__portfolio-item"
          :portfolio="portfolio"
          @click="gotToinvest(portfolio)"
        />
      </Grid>
    </main>
    <ButtonContainer
      v-if="renderAsView"
      class="wealth-invest__button-container"
    >
      <template #button-primary>
        <ButtonPrimary
          size="large"
          label="Criar nova carteira"
          @click="goToCatalog"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import {
  NavBar,
  Heading,
  Grid,
  LoaderIndicator,
  HelperViewStatus,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { PortfolioCardV2 } from '@/modules/wealth/components/PortfolioCardV2';

import { getPortfoliosToInvest } from '@/modules/wealth/services/investment';
import { IPortfolioList, PortfolioCategories } from '@/types/models/Portfolio';

withDefaults(
  defineProps<{
    renderAsView?: boolean;
  }>(),
  {
    renderAsView: true
  }
);

const router = useRouter();

const portfolioList = ref<IPortfolioList[]>([]);
const isLoading = ref(false);
const hasError = ref(false);

function gotToinvest(portfolio: IPortfolioList) {
  const routerName: Record<PortfolioCategories, string> = {
    [PortfolioCategories.Custom]: 'wealthProductTransaction',
    [PortfolioCategories.VariableIncome]: 'variableIncomeInvestment',
    [PortfolioCategories.WarrenManagement]: 'investment-transaction'
  };

  const paramsMapper: Record<PortfolioCategories, object> = {
    [PortfolioCategories.Custom]: { id: portfolio.id },
    [PortfolioCategories.VariableIncome]: { id: portfolio.id },
    [PortfolioCategories.WarrenManagement]: { portfolio: portfolio.id }
  };

  router.push({
    name: routerName[portfolio.type],
    params: { ...paramsMapper[portfolio.type] }
  });
}

function goToCatalog() {
  router.push({ name: 'catalog' });
}

async function fetchPortfolios() {
  isLoading.value = true;
  hasError.value = false;

  try {
    portfolioList.value = await getPortfoliosToInvest();
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  fetchPortfolios();
});
</script>

<style lang="less" scoped>
.wealth-invest {
  &__content {
    max-width: 832px;
    margin: 0 auto;
    padding: @size-spacing-x800 @size-spacing-x400 @size-spacing-x1800;
  }

  &__title {
    text-align: center;
    margin-bottom: @size-spacing-x1100;
  }

  &__portfolio-item {
    margin-bottom: @size-spacing-x500;
  }

  &__error {
    margin: 0 auto;
  }

  &__button-container {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: @background-secondary;
    z-index: 10;
  }
}
</style>
